/**
 * /settings/report-cards — tenant report card configuration (migration 0093).
 *
 * Edits the headline mood label set staff choose from in the composer and
 * the default sign-off line pre-filled into every card. Reads
 * GET /api/report-card-settings; saves via PUT (capability
 * facilities.configure — the composer itself reads via report_cards.send).
 */
import type { ReportCardSettingsView } from '@/lib/report_cards';
import { Loader2, Plus, RotateCcw, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

const LABEL_MAX = 60;
const LABELS_MAX_COUNT = 12;
const SIGN_OFF_MAX = 300;

export function ReportCardsSettings() {
  const [labels, setLabels] = useState<string[] | null>(null);
  const [signOff, setSignOff] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await fetch('/api/report-card-settings', { credentials: 'include' });
      if (!res.ok) throw new Error(`load failed: ${res.status}`);
      const body = (await res.json()) as { settings: ReportCardSettingsView };
      setLabels(body.settings.moodLabels);
      setSignOff(body.settings.signOffLine ?? '');
      setLoadError(null);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const addLabel = () => {
    const trimmed = newLabel.trim();
    if (!trimmed || !labels) return;
    if (labels.some((l) => l.toLowerCase() === trimmed.toLowerCase())) {
      setSaveError(`"${trimmed}" is already in the list.`);
      return;
    }
    if (labels.length >= LABELS_MAX_COUNT) {
      setSaveError(`You can have at most ${LABELS_MAX_COUNT} labels.`);
      return;
    }
    setSaveError(null);
    setLabels([...labels, trimmed]);
    setNewLabel('');
  };

  const removeLabel = (index: number) => {
    if (!labels) return;
    setLabels(labels.filter((_, i) => i !== index));
  };

  const save = async (payload: { moodLabels: string[] | null; signOffLine: string | null }) => {
    setSaving(true);
    setSaveError(null);
    try {
      const res = await fetch('/api/report-card-settings', {
        method: 'PUT',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { issues?: string[] };
        setSaveError(body.issues?.[0] ?? 'Could not save report card settings. Try again.');
        return;
      }
      const body = (await res.json()) as { settings: ReportCardSettingsView };
      setLabels(body.settings.moodLabels);
      setSignOff(body.settings.signOffLine ?? '');
      setSavedAt(Date.now());
    } finally {
      setSaving(false);
    }
  };

  if (loadError) {
    return (
      <div className="text-sm text-destructive" role="alert">
        {loadError}
      </div>
    );
  }
  if (labels === null) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="size-4 animate-spin" strokeWidth={1.75} />
        Loading…
      </div>
    );
  }

  return (
    <div className="max-w-2xl space-y-8">
      <header>
        <h1 className="text-xl font-semibold">Report cards</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Staff send report cards from any boarding stay or daycare visit. Choose the headline
          labels they can pick from and the sign-off line that appears on every card.
        </p>
      </header>

      <section>
        <h2 className="text-sm font-medium">Headline labels</h2>
        <p className="mt-0.5 text-xs text-muted-foreground">
          One is picked per card. Keep them short and warm.{' '}
          <span className="num">
            {labels.length}/{LABELS_MAX_COUNT}
          </span>
        </p>
        <ul className="mt-3 flex flex-wrap gap-2" data-testid="report-card-labels">
          {labels.map((label, index) => (
            <li
              key={label}
              className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background py-1 pl-3 pr-1.5 text-sm"
            >
              {label}
              <button
                type="button"
                onClick={() => removeLabel(index)}
                aria-label={`Remove ${label}`}
                disabled={labels.length <= 1}
                className="rounded-full p-0.5 text-muted-foreground hover:bg-muted disabled:opacity-40"
              >
                <X className="size-3.5" strokeWidth={1.75} />
              </button>
            </li>
          ))}
        </ul>
        <form
          className="mt-3 flex gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            addLabel();
          }}
        >
          <input
            type="text"
            value={newLabel}
            onChange={(e) => setNewLabel(e.currentTarget.value)}
            maxLength={LABEL_MAX}
            placeholder="Add a label, e.g. Full of beans"
            aria-label="New label"
            className="flex-1 rounded-md border border-border bg-background px-3 py-2 text-sm"
          />
          <button
            type="submit"
            disabled={!newLabel.trim()}
            className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-2 text-sm font-medium hover:bg-muted disabled:opacity-50"
          >
            <Plus className="size-4" strokeWidth={1.75} />
            Add label
          </button>
        </form>
      </section>

      <section>
        <label htmlFor="report-card-sign-off" className="text-sm font-medium">
          Default sign-off
        </label>
        <p className="mt-0.5 text-xs text-muted-foreground">
          Pre-filled into the composer; staff can edit it per card. Leave blank for none.
        </p>
        <input
          id="report-card-sign-off"
          type="text"
          value={signOff}
          onChange={(e) => setSignOff(e.currentTarget.value)}
          maxLength={SIGN_OFF_MAX}
          placeholder="With love from everyone at the kennel"
          className="mt-2 w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
        />
      </section>

      {saveError && (
        <p className="text-sm text-destructive" role="alert">
          {saveError}
        </p>
      )}

      <div className="flex items-center gap-3">
        <button
          type="button"
          disabled={saving || labels.length === 0}
          onClick={() =>
            void save({
              moodLabels: labels,
              signOffLine: signOff.trim().length > 0 ? signOff.trim() : null,
            })
          }
          className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
        >
          {saving && <Loader2 className="size-4 animate-spin" strokeWidth={1.75} />}
          Save changes
        </button>
        <button
          type="button"
          disabled={saving}
          onClick={() =>
            void save({
              moodLabels: null,
              signOffLine: signOff.trim().length > 0 ? signOff.trim() : null,
            })
          }
          className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-2 text-sm font-medium hover:bg-muted disabled:opacity-50"
        >
          <RotateCcw className="size-4" strokeWidth={1.75} />
          Restore default labels
        </button>
        {savedAt && !saving && (
          <span className="text-xs text-muted-foreground" data-testid="report-card-saved">
            Saved
          </span>
        )}
      </div>
    </div>
  );
}
